import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminLayout from "@/components/AdminLayout";
import PubLayout from "@/components/PubLayout";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminDailyOps from "@/pages/admin/AdminDailyOps";
import AdminBrands from "@/pages/admin/AdminBrands";
import AdminPauseCenter from "@/pages/admin/AdminPauseCenter";
import AdminClickCapRules from "@/pages/admin/AdminClickCapRules";
import AdminLedgerCorrections from "@/pages/admin/AdminLedgerCorrections";
import AdminBrandsSync from "@/pages/admin/AdminBrandsSync";
import AdminPublishers from "@/pages/admin/AdminPublishers";
import AdminPublisherDetail from "@/pages/admin/AdminPublisherDetail";
import AdminReports from "@/pages/admin/AdminReports";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminYieldkit from "@/pages/admin/AdminYieldkit";
import AdminYkClicks from "@/pages/admin/AdminYkClicks";
import AdminYkConversions from "@/pages/admin/AdminYkConversions";
import AdminSiteplug from "@/pages/admin/AdminSiteplug";
import AdminGiddyup from "@/pages/admin/AdminGiddyup";
import AdminRga from "@/pages/admin/AdminRga";
import AdminShopnomix from "@/pages/admin/AdminShopnomix";
import AdminAmazonFeed from "@/pages/admin/AdminAmazonFeed";
import AdminAdMedia from "@/pages/admin/AdminAdMedia";
import AdminMaxBounty from "@/pages/admin/AdminMaxBounty";
import AdminAutoArticle from "@/pages/admin/AdminAutoArticle";
import AdminFlexOffersReconciliation from "@/pages/admin/AdminFlexOffersReconciliation";
import AdminSystemHealth from "@/pages/admin/AdminSystemHealth";
import AdminDiagnostics from "@/pages/admin/AdminDiagnostics";
import AdminCoverageMatrix from "@/pages/admin/AdminCoverageMatrix";
import AdminPublisherPerformance from "@/pages/admin/AdminPublisherPerformance";
import AdminClickLog from "@/pages/admin/AdminClickLog";
import AdminRoutedBrands from "@/pages/admin/AdminRoutedBrands";
import AdminRefPolicies from "@/pages/admin/AdminRefPolicies";
import AdminDeepSearch from "@/pages/admin/AdminDeepSearch";
import AdminBackfillAttribution from "@/pages/admin/AdminBackfillAttribution";
import PubDashboard from "@/pages/publisher/PubDashboard";
import PubBrands from "@/pages/publisher/PubBrands";
import PubReports from "@/pages/publisher/PubReports";
import PubBankDetails from "@/pages/publisher/PubBankDetails";
import PubIntegrations from "@/pages/publisher/PubIntegrations";
import PubCPC from "@/pages/publisher/PubCPC";
import PubAmazon from "@/pages/publisher/PubAmazon";
import PubProfile from "@/pages/publisher/PubProfile";
import PublicReport from "@/pages/PublicReport";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/publisher"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/r/:token" element={<PublicReport />} />

          <Route path="/admin" element={<ProtectedRoute role="admin"><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="daily-ops" element={<AdminDailyOps />} />
            <Route path="brands" element={<AdminBrands />} />
            <Route path="brands-sync" element={<AdminBrandsSync />} />
            <Route path="publishers" element={<AdminPublishers />} />
            <Route path="publishers/:id" element={<AdminPublisherDetail />} />
            <Route path="pause-center" element={<AdminPauseCenter />} />
            <Route path="ledger-corrections" element={<AdminLedgerCorrections />} />
            <Route path="click-cap-rules" element={<AdminClickCapRules />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="click-log" element={<AdminClickLog />} />
            <Route path="routed-brands" element={<AdminRoutedBrands />} />
            <Route path="ref-policies" element={<AdminRefPolicies />} />
            <Route path="deep-search" element={<AdminDeepSearch />} />
            <Route path="backfill" element={<AdminBackfillAttribution />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="audit-log" element={<AdminAuditLog />} />
            <Route path="yieldkit" element={<AdminYieldkit />} />
            <Route path="yieldkit/clicks" element={<AdminYkClicks />} />
            <Route path="yieldkit/conversions" element={<AdminYkConversions />} />
            <Route path="siteplug" element={<AdminSiteplug />} />
            <Route path="giddyup" element={<AdminGiddyup />} />
            <Route path="rga" element={<AdminRga />} />
            <Route path="shopnomix" element={<AdminShopnomix />} />
            <Route path="amazon-feed/:slug" element={<AdminAmazonFeed />} />
            <Route path="admedia" element={<AdminAdMedia />} />
            <Route path="maxbounty" element={<AdminMaxBounty />} />
            <Route path="auto-article" element={<AdminAutoArticle />} />
            <Route path="flexoffers-reconciliation" element={<AdminFlexOffersReconciliation />} />
            <Route path="health" element={<AdminSystemHealth />} />
            <Route path="diagnostics" element={<AdminDiagnostics />} />
            <Route path="coverage-matrix" element={<AdminCoverageMatrix />} />
            <Route path="publisher-performance" element={<AdminPublisherPerformance />} />
            <Route path="assignment-overview" element={<Navigate to="/admin/publisher-performance" replace />} />
            <Route path="main-table" element={<Navigate to="/admin/publisher-performance" replace />} />
          </Route>

          <Route path="/publisher" element={<ProtectedRoute role="publisher"><PubLayout /></ProtectedRoute>}>
            <Route index element={<PubDashboard />} />
            <Route path="brands" element={<PubBrands />} />
            <Route path="cpc" element={<PubCPC />} />
            <Route path="amazon" element={<PubAmazon />} />
            <Route path="reports" element={<PubReports />} />
            <Route path="integrations" element={<PubIntegrations />} />
            <Route path="postback" element={<PubIntegrations />} />
            <Route path="bank-details" element={<PubBankDetails />} />
            <Route path="profile" element={<PubProfile />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
